import { useState, useRef, useEffect } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import EarlyAccessSignupModal from "../../../components/feature/EarlyAccessSignupModal";

export default function HeroSection() {
  const [showModal, setShowModal] = useState(false);
  const { isDark } = useTheme();

  // Reveal state
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [leftIn, setLeftIn] = useState(false);
  const [rightIn, setRightIn] = useState(false);
  const [bottomIn, setBottomIn] = useState(false);

  useEffect(() => {
    const observe = (
      el: HTMLElement | null,
      setter: (v: boolean) => void,
      threshold = 0.05
    ) => {
      if (!el) return () => {};
      const obs = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setter(true);
            obs.unobserve(el);
          }
        },
        { threshold }
      );
      obs.observe(el);
      return () => obs.disconnect();
    };

    const c1 = observe(leftRef.current, setLeftIn);
    const c2 = observe(rightRef.current, setRightIn);
    const c3 = observe(bottomRef.current, setBottomIn);
    return () => { c1(); c2(); c3(); };
  }, []);

  const rv = (
    dir: "left" | "right" | "up" | "fade",
    inView: boolean,
    delay = 0
  ) => ({
    className: `reveal-${dir}${inView ? " in-view" : ""}`,
    style: { transitionDelay: `${delay}ms` } as React.CSSProperties,
  });

  return (
    <section
      className={`relative min-h-fit lg:min-h-screen flex items-center overflow-hidden transition-colors duration-300 ${
        isDark ? "bg-[#050505]" : "bg-white"
      }`}
      style={{
        backgroundImage: isDark
          ? "radial-gradient(ellipse 70% 60% at 15% 40%, rgba(255,255,255,0.025) 0%, transparent 70%), radial-gradient(ellipse 60% 55% at 85% 65%, rgba(255,255,255,0.018) 0%, transparent 70%)"
          : "radial-gradient(ellipse 70% 60% at 15% 40%, rgba(0,0,0,0.022) 0%, transparent 70%), radial-gradient(ellipse 60% 55% at 85% 65%, rgba(0,0,0,0.016) 0%, transparent 70%)",
      }}
    >
      {/* Background grid */}
      <div
        className={`absolute inset-0 pointer-events-none ${
          isDark ? "opacity-[0.035]" : "opacity-[0.04]"
        }`}
        style={{
          backgroundImage: isDark
            ? "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)"
            : "linear-gradient(rgba(0,0,0,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.08) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* Static soft center glow — GPU painted once, zero animation cost */}
      <div
        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full blur-3xl pointer-events-none ${
          isDark ? "bg-white/[0.02]" : "bg-black/[0.02]"
        }`}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-16 pt-20 pb-8 lg:pt-28 lg:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left Content */}
          <div ref={leftRef}>
            {/* Eyebrow */}
            <div
              {...rv("up", leftIn, 0)}
              className={`${rv("up", leftIn, 0).className} inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4 lg:mb-8 border ${
                isDark ? "bg-white/5 border-white/10" : "bg-black/4 border-black/8"
              }`}
              style={rv("up", leftIn, 0).style}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full inline-block ${
                  isDark ? "bg-[#e0e0e0]" : "bg-[#444444]"
                }`}
              />
              <span
                className={`font-inter text-[10px] lg:text-xs uppercase tracking-widest ${
                  isDark ? "text-[#999999]" : "text-[#666666]"
                }`}
              >
                Built for Serious Resellers
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-syne font-bold leading-[1.05] mb-3 lg:mb-6">
              <span
                className={`block text-[2rem] sm:text-5xl lg:text-7xl xl:text-[78px] reveal-left${leftIn ? " in-view" : ""} ${
                  isDark ? "text-white" : "text-[#111111]"
                }`}
                style={{ transitionDelay: "120ms" }}
              >
                Track Every
              </span>
              <span
                className={`block text-[2rem] sm:text-5xl lg:text-7xl xl:text-[78px] reveal-right${leftIn ? " in-view" : ""} ${
                  isDark ? "text-white" : "text-[#111111]"
                }`}
                style={{ transitionDelay: "240ms" }}
              >
                Dollar You
              </span>
              <span
                className={`block text-[2rem] sm:text-5xl lg:text-7xl xl:text-[78px] text-transparent bg-clip-text reveal-left${leftIn ? " in-view" : ""} ${
                  isDark
                    ? "bg-gradient-to-r from-white to-[#555555]"
                    : "bg-gradient-to-r from-[#111111] to-[#888888]"
                }`}
                style={{ transitionDelay: "360ms" }}
              >
                Resell.
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className={`font-inter text-sm lg:text-lg leading-relaxed max-w-[480px] mb-4 lg:mb-10 reveal-up${leftIn ? " in-view" : ""} ${
                isDark ? "text-[#666666]" : "text-[#555555]"
              }`}
              style={{ transitionDelay: "500ms" }}
            >
              Most resellers lose 15–22% to fees they never see. Trackly
              automatically calculates every platform fee, shipping cost, and
              hidden charge — so you always know your real net profit and ROI.
            </p>

            {/* CTA */}
            <div
              className={`mb-4 lg:mb-10 reveal-up${leftIn ? " in-view" : ""}`}
              id="early-access-section"
              style={{ transitionDelay: "640ms" }}
            >
              <button
                onClick={() => setShowModal(true)}
                className={`group inline-flex items-center gap-2 lg:gap-3 font-inter font-semibold text-sm lg:text-base px-7 py-3 lg:px-10 lg:py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  isDark
                    ? "bg-white text-black hover:bg-[#e8e8e8] hover:gap-4"
                    : "bg-[#111111] text-white hover:bg-[#2a2a2a] hover:gap-4"
                }`}
              >
                Join Early Access
                <span
                  className={`w-5 h-5 lg:w-6 lg:h-6 flex items-center justify-center rounded-full transition-all duration-300 ${
                    isDark ? "bg-black/10" : "bg-white/15"
                  }`}
                >
                  <i className="ri-arrow-right-line text-xs lg:text-sm" />
                </span>
              </button>
              <p
                className={`font-inter text-[10px] lg:text-xs mt-3 ml-1 ${
                  isDark ? "text-[#555555]" : "text-[#888888]"
                }`}
              >
                Free early access · No credit card required · Join 800+ resellers
              </p>
            </div>

            {/* Key messaging pills */}
            <div
              className={`flex flex-wrap gap-2 lg:gap-3 reveal-up${leftIn ? " in-view" : ""}`}
              style={{ transitionDelay: "760ms" }}
            >
              {["No spreadsheets", "No guessing", "Real margins, instantly"].map(
                (tag) => (
                  <span
                    key={tag}
                    className={`font-inter text-[10px] lg:text-xs rounded-full px-3 py-1 lg:px-4 lg:py-1.5 border ${
                      isDark
                        ? "text-[#666666] bg-white/3 border-white/8"
                        : "text-[#666666] bg-black/4 border-black/8"
                    }`}
                  >
                    {tag}
                  </span>
                )
              )}
            </div>

            {/* Social handles — hidden on mobile */}
            <div
              className={`flex items-center gap-6 mt-8 reveal-up${leftIn ? " in-view" : ""}`}
              style={{ transitionDelay: "880ms" }}
            >
              <span
                className={`font-inter text-xs whitespace-nowrap ${
                  isDark ? "text-[#444444]" : "text-[#bbbbbb]"
                }`}
              >
                Follow us
              </span>
              <div className="flex items-center gap-3">
                {[
                  {
                    icon: "ri-twitter-x-fill",
                    label: "X (Twitter)",
                    href: "https://x.com/usetrackly",
                    imgSrc: "https://storage.readdy-site.link/project_files/d2fca44a-eb94-4797-9168-15f21f7a5589/7c4a3f62-9d76-40b6-9e43-3fce62dc6c6e_X-Logo.png?v=9928d9c65f8e3db4a5a51f88e3be6a7d",
                    invertOnDark: true,
                    imgSize: "w-5 h-5",
                  },
                  {
                    icon: "ri-instagram-fill",
                    label: "Instagram",
                    href: "https://instagram.com/usetrackly",
                    imgSrc: "https://static.readdy.ai/image/1de166adf1bbf13a9e4054af96cf4ae8/907fda1763aab42155461943ef6de0d8.webp",
                    invertOnDark: false,
                    imgSize: "w-4 h-4",
                  },
                  {
                    icon: "ri-tiktok-fill",
                    label: "TikTok",
                    href: "https://tiktok.com/@usetrackly",
                    imgSrc: "https://storage.readdy-site.link/project_files/d2fca44a-eb94-4797-9168-15f21f7a5589/68f04236-6d28-4cf2-a183-0dc76966705b_tiktok-logo-tikok-icon-transparent-tikok-app-logo-free-png.png?v=f4b9689355e39ba27d0fdaba5b509f5b",
                    invertOnDark: true,
                    imgSize: "w-5 h-5",
                  },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="nofollow noreferrer"
                    title={`${s.label} — @usetrackly`}
                    className="group flex flex-col items-center gap-1.5 cursor-pointer transition-all duration-200"
                  >
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-200 ${
                        isDark
                          ? "bg-white/5 border-white/10 group-hover:bg-white/12 group-hover:border-white/20"
                          : "bg-black/5 border-black/10 group-hover:bg-black/10 group-hover:border-black/20"
                      }`}
                    >
                      {s.imgSrc ? (
                        <img
                          src={s.imgSrc}
                          alt={s.label}
                          className={`${s.imgSize ?? "w-4 h-4"} object-contain transition-all duration-200 ${s.invertOnDark && isDark ? "invert" : ""}`}
                        />
                      ) : (
                        <i
                          className={`${s.icon} text-lg ${
                            isDark
                              ? "text-[#888888] group-hover:text-white"
                              : "text-[#777777] group-hover:text-[#111111]"
                          } transition-colors duration-200`}
                        />
                      )}
                    </div>
                    <span
                      className={`font-inter text-[10px] ${
                        isDark
                          ? "text-[#444444] group-hover:text-[#888888]"
                          : "text-[#bbbbbb] group-hover:text-[#777777]"
                      } transition-colors duration-200`}
                    >
                      {s.label === "X (Twitter)" ? "X" : s.label}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Calculator Preview — hidden on mobile */}
          <div
            ref={rightRef}
            className={`hidden lg:block relative reveal-right${rightIn ? " in-view" : ""}`}
            style={{ transitionDelay: "200ms" }}
          >
            <div
              className={`border rounded-2xl p-6 transition-colors duration-300 ${
                isDark
                  ? "bg-[#0d0d0d] border-white/10"
                  : "bg-[#f0f0f0] border-black/8"
              }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <span
                  className={`font-inter text-xs uppercase tracking-widest ${
                    isDark ? "text-[#666666]" : "text-[#888888]"
                  }`}
                >
                  Flip Calculator
                </span>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full animate-pulse ${
                      isDark ? "bg-[#e0e0e0]" : "bg-[#444444]"
                    }`}
                  />
                  <span
                    className={`font-inter text-xs ${
                      isDark ? "text-[#666666]" : "text-[#888888]"
                    }`}
                  >
                    Live preview
                  </span>
                </div>
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: "Buy Price", value: "$45.00" },
                  { label: "Sale Price", value: "$120.00" },
                  { label: "Platform", value: "eBay" },
                  { label: "Shipping", value: "$8.50" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={`border rounded-xl p-3.5 ${
                      isDark
                        ? "bg-[#111111] border-white/5"
                        : "bg-white border-black/8"
                    }`}
                  >
                    <p
                      className={`font-inter text-xs mb-1 ${
                        isDark ? "text-[#555555]" : "text-[#888888]"
                      }`}
                    >
                      {item.label}
                    </p>
                    <p
                      className={`font-inter text-sm ${
                        isDark ? "text-[#999999]" : "text-[#444444]"
                      }`}
                    >
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              <div
                className={`border-t mb-5 ${
                  isDark ? "border-white/5" : "border-black/5"
                }`}
              />

              {/* Fee Breakdown */}
              <div className="flex flex-col gap-2.5 mb-5">
                <p
                  className={`font-inter text-xs uppercase tracking-widest mb-1 ${
                    isDark ? "text-[#555555]" : "text-[#888888]"
                  }`}
                >
                  Fee Breakdown
                </p>
                {[
                  { label: "eBay Final Value Fee (13.25%)", value: "-$15.90" },
                  { label: "Payment Processing (2.9%)", value: "-$3.57" },
                  { label: "Shipping Cost", value: "-$8.50" },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between"
                  >
                    <span
                      className={`font-inter text-xs ${
                        isDark ? "text-[#666666]" : "text-[#777777]"
                      }`}
                    >
                      {row.label}
                    </span>
                    <span
                      className={`font-inter text-xs ${
                        isDark ? "text-[#555555]" : "text-[#888888]"
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Result */}
              <div
                className={`border rounded-xl p-4 ${
                  isDark
                    ? "bg-white/4 border-white/8"
                    : "bg-black/4 border-black/8"
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className={`font-inter text-sm ${
                      isDark ? "text-[#888888]" : "text-[#555555]"
                    }`}
                  >
                    Net Profit
                  </span>
                  <span
                    className={`font-syne font-bold text-2xl ${
                      isDark ? "text-white" : "text-[#111111]"
                    }`}
                  >
                    $47.03
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`font-inter text-xs ${
                      isDark ? "text-[#666666]" : "text-[#777777]"
                    }`}
                  >
                    Return on Investment
                  </span>
                  <span
                    className={`font-inter text-sm font-medium ${
                      isDark ? "text-[#888888]" : "text-[#555555]"
                    }`}
                  >
                    +104.5% ROI
                  </span>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div
              className={`absolute -bottom-4 -right-4 border rounded-xl px-4 py-3 ${
                isDark
                  ? "bg-[#111111] border-white/10"
                  : "bg-white border-black/10"
              }`}
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i
                    className={`ri-eye-off-line text-sm ${
                      isDark ? "text-[#888888]" : "text-[#555555]"
                    }`}
                  />
                </div>
                <div>
                  <p
                    className={`font-syne font-bold text-sm ${
                      isDark ? "text-white" : "text-[#111111]"
                    }`}
                  >
                    $27.97
                  </p>
                  <p
                    className={`font-inter text-xs ${
                      isDark ? "text-[#666666]" : "text-[#777777]"
                    }`}
                  >
                    in hidden fees found
                  </p>
                </div>
              </div>
            </div>

            <div
              className={`absolute inset-0 rounded-2xl blur-3xl -z-10 scale-95 ${
                isDark ? "bg-white/2" : "bg-black/2"
              }`}
            />
          </div>
        </div>

        {/* Platform logos bar — hidden on mobile */}
        <div
          ref={bottomRef}
          className={`hidden lg:block mt-20 pt-10 border-t reveal-up${bottomIn ? " in-view" : ""} ${
            isDark ? "border-white/5" : "border-black/5"
          }`}
          style={{ transitionDelay: "0ms" }}
        >
          <p
            className={`font-inter text-xs text-center uppercase tracking-widest mb-6 ${
              isDark ? "text-[#555555]" : "text-[#888888]"
            }`}
          >
            Supports all major selling platforms
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3">
            {["eBay", "StockX", "GOAT", "Poshmark", "Whatnot", "Mercari", "Depop", "Facebook"].map(
              (brand) => (
                <span
                  key={brand}
                  className={`font-syne font-semibold text-sm tracking-wide ${
                    isDark ? "text-[#2a2a2a]" : "text-[#cccccc]"
                  }`}
                >
                  {brand}
                </span>
              )
            )}
          </div>
        </div>
      </div>

      {showModal && <EarlyAccessSignupModal onClose={() => setShowModal(false)} />}
    </section>
  );
}
